import {
  collection, query, where, orderBy, limit, getDocs,
  doc, getDoc, setDoc, updateDoc, deleteDoc, startAfter,
  serverTimestamp, deleteField, increment, arrayUnion, arrayRemove,
  onSnapshot,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../firebase.js';

export const REPORT_REASONS = ['spam', 'nsfw', 'copyright', 'other'];

export const DesignService = {
  // Fetch user's designs (paginated). `spaceCategory` (Phase 9-5) and
  // `styleFilter` mirror the community feed's filters so My Designs gets
  // the same category + style narrowing UX.
  async getUserDesigns(userId, { lastDoc = null, pageSize = 18, favoritesOnly = false, spaceCategory = null, styleFilter = null } = {}) {
    const constraints = [
      where('userId', '==', userId),
      where('status', '==', 'success'),
    ];
    if (favoritesOnly) constraints.push(where('isFavorite', '==', true));
    if (spaceCategory) constraints.push(where('spaceCategory', '==', spaceCategory));
    if (styleFilter) constraints.push(where('style', '==', styleFilter));
    constraints.push(orderBy('timestamp', 'desc'));
    constraints.push(limit(pageSize));

    let q = query(collection(db, 'designs'), ...constraints);
    if (lastDoc) q = query(q, startAfter(lastDoc));

    const snap = await getDocs(q);
    const designs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const lastVisible = snap.docs[snap.docs.length - 1] || null;
    return { designs, lastVisible, hasMore: snap.docs.length === pageSize };
  },

  // Get single design by ID
  async getDesign(designId) {
    const snap = await getDoc(doc(db, 'designs', designId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  // Fetch designs that were edited from this one (Phase 9-3 lineage UI).
  // Returns array of { id, ... } sorted by recency. Caps at `max` to keep
  // DesignDetail rendering snappy.
  async getEditsOfDesign(designId, { max = 6 } = {}) {
    if (!designId) return [];
    try {
      const q = query(
        collection(db, 'designs'),
        where('sourceDesignId', '==', designId),
        where('status', '==', 'success'),
        orderBy('timestamp', 'desc'),
        limit(max),
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('getEditsOfDesign failed:', e);
      return [];
    }
  },

  // ─── Bookmarks (Phase 10-1) ────────────────────────────────────────
  // users/{uid}/bookmarks/{designId} — owner-only. Doc presence == bookmarked.
  async toggleBookmark(designId, currentlyBookmarked) {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) throw new Error('AUTH_REQUIRED');
    const ref = doc(db, 'users', user.uid, 'bookmarks', designId);
    if (currentlyBookmarked) {
      await deleteDoc(ref);
    } else {
      await setDoc(ref, { designId, savedAt: serverTimestamp() });
    }
    return !currentlyBookmarked;
  },

  async isBookmarked(designId) {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) return false;
    const snap = await getDoc(doc(db, 'users', user.uid, 'bookmarks', designId));
    return snap.exists();
  },

  // Subscribe to a single bookmark for live state in detail/share views.
  subscribeBookmark(designId, cb) {
    const user = auth.currentUser;
    if (!user || user.isAnonymous || !designId) { cb(false); return () => {}; }
    return onSnapshot(
      doc(db, 'users', user.uid, 'bookmarks', designId),
      (snap) => cb(snap.exists()),
      () => cb(false),
    );
  },

  // Fetch the user's bookmarks page (paginated). Resolves bookmark docs into
  // full design payloads via batched reads, drops bookmarks pointing at
  // designs that have since been deleted.
  async getBookmarkedDesigns(uid, { lastDoc = null, pageSize = 18 } = {}) {
    if (!uid) return { designs: [], lastVisible: null, hasMore: false };
    let q = query(
      collection(db, 'users', uid, 'bookmarks'),
      orderBy('savedAt', 'desc'),
      limit(pageSize),
    );
    if (lastDoc) q = query(q, startAfter(lastDoc));
    const snap = await getDocs(q);
    const ids = snap.docs.map(d => d.id);
    const designs = await Promise.all(
      ids.map(async (id) => {
        const ds = await getDoc(doc(db, 'designs', id));
        return ds.exists() ? { id: ds.id, ...ds.data() } : null;
      }),
    );
    return {
      designs: designs.filter(Boolean),
      lastVisible: snap.docs[snap.docs.length - 1] || null,
      hasMore: snap.docs.length === pageSize,
    };
  },

  // Toggle favorite
  async toggleFavorite(designId, currentValue) {
    await updateDoc(doc(db, 'designs', designId), {
      isFavorite: !currentValue,
    });
    return !currentValue;
  },

  // Rename design
  async renameDesign(designId, name) {
    await updateDoc(doc(db, 'designs', designId), { name: name.trim() });
  },

  // Delete design and its Storage files
  async deleteDesign(design) {
    // Delete Storage files (best effort)
    const urls = [
      ...(design.originalImageUrls || []),
      ...(design.generatedImageUrls || []),
    ].filter(Boolean);

    await Promise.allSettled(
      urls.map(url => {
        try {
          const fileRef = ref(storage, url);
          return deleteObject(fileRef);
        } catch {
          return Promise.resolve();
        }
      })
    );

    await deleteDoc(doc(db, 'designs', design.id));
  },

  // Set public/private
  async setPublic(designId, isPublic) {
    await updateDoc(doc(db, 'designs', designId), { isPublic });
  },

  // Set community feed listing (listing also makes the design publicly accessible).
  // listedAt 은 *피드 공개 시점* 으로 Latest 정렬 키 — 디자인 생성 후 며칠 묵혀
  // 공개해도 Latest 상단에 뜨도록. 매 list/relist 때 갱신해서 재공개 시 다시
  // 상단으로 끌어올리는 효과 (Instagram·Pinterest 식).
  async setListed(designId, isListed, caption = undefined) {
    const update = { isListed };
    if (isListed) {
      update.isPublic = true;
      update.listedAt = serverTimestamp();
      // Initialize likeCount if missing so popular sort index works
      update.likeCount = increment(0);
    }
    // caption: undefined → 변경 없음. string → 설정. '' → 지움 (null 로 저장하면 firestore 가 필드 자체를 보존하지만 빈 string 이 더 단순).
    if (caption !== undefined) {
      update.caption = String(caption).slice(0, 280);
    }
    await updateDoc(doc(db, 'designs', designId), update);
  },

  // 게시된 디자인의 caption 만 수정. 게시 상태와 무관.
  async updateCaption(designId, caption) {
    await updateDoc(doc(db, 'designs', designId), {
      caption: String(caption || '').slice(0, 280),
    });
  },

  // Toggle like (like or unlike a design)
  async toggleLike(designId, userId, currentlyLiked) {
    const ref = doc(db, 'designs', designId);
    if (currentlyLiked) {
      await updateDoc(ref, { likeCount: increment(-1), likedBy: arrayRemove(userId) });
    } else {
      await updateDoc(ref, { likeCount: increment(1), likedBy: arrayUnion(userId) });
    }
  },

  // Get public feed designs (paginated). When `userIds` is provided (Phase
  // 10-2 Following tab), filters to designs by those uids — capped at 30 by
  // the Firestore `in` operator. Empty userIds returns no results.
  // `spaceCategory` (Phase 9-5) filters by interior / exterior / garden;
  // null means show all categories.
  async getFeedDesigns({ lastDoc = null, pageSize = 12, sortBy = 'latest', styleFilter = null, userIds = null, spaceCategory = null } = {}) {
    if (userIds && userIds.length === 0) {
      return { designs: [], lastVisible: null, hasMore: false };
    }
    const constraints = [
      where('isListed', '==', true),
      where('status', '==', 'success'),
    ];
    if (userIds) {
      constraints.push(where('userId', 'in', userIds.slice(0, 30)));
    }
    if (styleFilter) constraints.push(where('style', '==', styleFilter));
    if (spaceCategory) constraints.push(where('spaceCategory', '==', spaceCategory));
    // 피드 정렬은 *공개 시점* (listedAt) 기준 — 디자인 생성 시점 (timestamp) 대신.
    // 백필로 기존 listed 디자인들도 listedAt = 기존 timestamp 로 채워둠.
    if (sortBy === 'popular') {
      constraints.push(orderBy('likeCount', 'desc'));
      constraints.push(orderBy('listedAt', 'desc'));
    } else {
      constraints.push(orderBy('listedAt', 'desc'));
    }
    constraints.push(limit(pageSize));

    let q = query(collection(db, 'designs'), ...constraints);
    if (lastDoc) q = query(q, startAfter(lastDoc));

    const snap = await getDocs(q);
    return {
      designs: snap.docs.map(d => ({ id: d.id, ...d.data() })),
      lastVisible: snap.docs[snap.docs.length - 1] || null,
      hasMore: snap.docs.length === pageSize,
    };
  },

  // Mark a design for ownership claim (called while still anonymous, before account switch)
  async markForClaim(designId) {
    const token = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    await updateDoc(doc(db, 'designs', designId), { claimToken: token });
    return token;
  },

  // Claim ownership of a marked design (called after signing in as new account)
  async claimDesign(designId) {
    await updateDoc(doc(db, 'designs', designId), {
      userId: auth.currentUser.uid,
      claimToken: deleteField(),
    });
  },

  // Submit a content report. Doc ID is `${uid}_${designId}` so re-submissions
  // by the same user collapse to one write and the aggregation trigger only
  // fires once. Returns true if a new report was filed.
  async submitReport(designId, reason, note = '') {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) throw new Error('SIGN_IN_REQUIRED');
    if (!REPORT_REASONS.includes(reason)) throw new Error('INVALID_REASON');
    const id = `${user.uid}_${designId}`;
    const payload = {
      designId,
      reporterId: user.uid,
      reason,
      createdAt: serverTimestamp(),
    };
    if (note) payload.note = note.slice(0, 500);
    await setDoc(doc(db, 'reports', id), payload);
    return true;
  },

  // Format timestamp for display
  // lang = 앱 언어 (useLocale). 예전엔 'en-US' 고정이라 모든 언어에서 "May 23, 2026" 이 나왔다.
  formatDate(timestamp, lang) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(lang || 'en', { year: 'numeric', month: 'short', day: 'numeric' });
  },
};
